import { calculateTotalCost } from '@jobos/shared';
import type { BreakdownSnapshot, Rates, SubItemCategory, SubItemTypeMetaMap } from '@jobos/shared';
import { snapshotToJobBreakdown } from './templateConversions';
import { toFlatItems, type FlatItem } from './types';
import type { LineItemData, ProjectData } from './LineItemList';

export type ApplyTemplateMode = 'replace' | 'append';

export interface ApplyTemplateArgs {
  snapshot: BreakdownSnapshot;
  mode: ApplyTemplateMode;
  existingFlatItems: FlatItem[];
  currentRates: Rates;
  currentRounding: number | null;
  typeMeta: SubItemTypeMetaMap;
  categories: SubItemCategory[];
}

export interface ApplyTemplateResult {
  flatItems: FlatItem[];
  rates: Rates;
  roundingPrecision: number | null;
}

/**
 * True when the stored snapshot actually carries rates. Templates predating
 * contents-editing do not, and `snapshotToJobBreakdown` defaults a missing rate
 * to 0 — adopting that would silently zero the job's tax and markup.
 */
export function snapshotHasRates(snapshot: BreakdownSnapshot): boolean {
  return snapshot.taxRate != null && snapshot.markupRate != null;
}

/**
 * Convert a job-type template's stored snapshot into editor state, either
 * replacing the open breakdown or appending to it.
 *
 * The caller pushes the result through initFlatItems / setJobRates /
 * setRoundingPrecision, so the existing autosave diff persists it and undo
 * still works — no dedicated backend endpoint is involved.
 */
export function applyTemplateSnapshot({
  snapshot,
  mode,
  existingFlatItems,
  currentRates,
  currentRounding,
  typeMeta,
  categories,
}: ApplyTemplateArgs): ApplyTemplateResult {
  // Append never adopts template rates — merging two templates' rates has no
  // sensible meaning, so the job keeps its own.
  const adoptRates = mode === 'replace' && snapshotHasRates(snapshot);

  const rates: Rates = adoptRates
    ? { taxRate: Number(snapshot.taxRate), markupRate: Number(snapshot.markupRate) }
    : currentRates;
  const roundingPrecision = adoptRates
    ? snapshot.roundingPrecision == null
      ? null
      : Number(snapshot.roundingPrecision)
    : currentRounding;

  // Stored per-line totalCost may be stale (category settings can change after
  // the template was saved), so recompute under the effective rates.
  const templateFlat = toFlatItems(snapshotToJobBreakdown(snapshot)).map((item) => {
    if (item.type !== 'line-item') return item;
    const li = item.data as LineItemData;
    return {
      ...item,
      data: { ...li, totalCost: calculateTotalCost(li.subItems, rates, typeMeta, categories) },
    };
  });

  if (mode === 'replace') {
    return { flatItems: templateFlat, rates, roundingPrecision };
  }

  // Only top-level entries (loose line items and project headers) share a
  // sortOrder space; items nested in a project sort within their project.
  const isTopLevel = (item: FlatItem) =>
    item.type === 'project-header' || item.projectId == null;

  const offset =
    existingFlatItems.reduce((max, item) => {
      if (!isTopLevel(item)) return max;
      return Math.max(max, (item.data as LineItemData | ProjectData).sortOrder ?? 0);
    }, -1) + 1;

  const shifted = templateFlat.map((item) => {
    if (!isTopLevel(item)) return item;
    const data = item.data as LineItemData | ProjectData;
    return { ...item, data: { ...data, sortOrder: (data.sortOrder ?? 0) + offset } };
  });

  return { flatItems: [...existingFlatItems, ...shifted], rates, roundingPrecision };
}
